import * as Sentry from '@sentry/react';

/**
 * Utility functions for Sentry error reporting
 * All functions check for production mode and only send to Sentry in production
 */

export const reportError = (
  error: Error,
  context?: Record<string, any>,
  tags?: Record<string, string>
) => {
  if (import.meta.env.PROD) {
    Sentry.withScope((scope) => {
      // Add context if provided
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value);
        });
      }

      // Add tags if provided
      if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      scope.setLevel('error');
      Sentry.captureException(error);
    });
  } else {
    // In development, just log to console
    console.error('Error reported to Sentry (dev mode):', error, { context, tags });
  }
};

export const addBreadcrumb = (
  message: string,
  category: string,
  level: 'info' | 'warning' | 'error' | 'debug' = 'info',
  data?: Record<string, any>
) => {
  if (import.meta.env.PROD) {
    Sentry.addBreadcrumb({
      message,
      category,
      level,
      data,
      timestamp: Date.now() / 1000
    });
  } else {
    // In development, just log to console
    console.log(`Sentry breadcrumb (dev mode) [${level}] [${category}]:`, message, { data });
  }
};

// User context functions - use these instead of direct Sentry calls
export const setUser = (user: { id: string; email: string; username?: string }) => {
  if (import.meta.env.PROD) {
    Sentry.setUser(user);
  } else {
    console.log('Sentry user context set (dev mode):', user);
  }
};

export const clearUser = () => {
  if (import.meta.env.PROD) {
    Sentry.setUser(null);
  } else {
    console.log('Sentry user context cleared (dev mode)');
  }
};
